import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  Fade,
  CircularProgress,
  Tooltip,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TemplateIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useGlobalSettingState, useComponentWebSocket } from './GlobalSettingUtils';
import TagChip from '../Common/TagChip';

const TemplateSettings = forwardRef((props, ref) => {
  const { 
    t,
    openSnackbar
  } = useGlobalSettingState();

  const [templates, setTemplates] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exportingIds, setExportingIds] = useState(new Set());
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [pendingImportData, setPendingImportData] = useState(null);

  // Define handlers for WebSocket messages
  const commandHandlers = {
    'getAllTemplates': (message) => {
      // Handle both direct message and data-wrapped message
      const data = message.data || message;
      if (data.status === 'success') {
        console.log('Templates received:', data.templates);
        setTemplates(data.templates || []);
        setLoading(false);
      } else {
        console.error('Templates load error:', data);
        openSnackbar(t('global_setting.templates_load_error', 'Error loading templates'), 3000);
        setLoading(false);
      }
    },
    'deleteTemplate': (message) => {
      // Handle both direct message and data-wrapped message
      const data = message.data || message;
      setDeleting(false); // Stop delete loading state
      if (data.status === 'success') {
        openSnackbar(t('global_setting.template_deleted', 'Template deleted successfully'), 3000);
        // Refresh templates list
        setTimeout(() => {
          setLoading(true);
          sendCommand('getAllTemplates');
        }, 100);
      } else {
        openSnackbar(t('global_setting.template_delete_error', 'Error deleting template'), 3000);
      }
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    },
    'importTemplate': (message) => {
      // Handle both direct message and data-wrapped message
      const data = message.data || message;
      setImporting(false); // Stop import loading state
      if (data.status === 'success') {
        openSnackbar(t('global_setting.template_imported', 'Template imported successfully'), 3000);
        // Refresh templates list
        setTimeout(() => {
          setLoading(true);
          sendCommand('getAllTemplates');
        }, 100);
      } else if (data.status === 'skipped') {
        openSnackbar(
          data.message || t('global_setting.template_skipped', 'Template already exists - import skipped'), 
          4000
        );
        // No need to refresh since nothing changed
      } else {
        openSnackbar(data.message || t('global_setting.template_import_error', 'Error importing template'), 3000);
      }
    },
    'importTemplates': (message) => {
      // Handle bulk import response
      const data = message.data || message;
      setImporting(false); // Stop import loading state
      
      if (data.status === 'success') {
        if (data.skipped_count && data.skipped_count > 0) {
          openSnackbar(
            t('global_setting.bulk_import_success_with_skips', 
              'Imported {{imported_count}} templates. {{skipped_count}} already existed and were skipped.',
              { imported_count: data.imported_count, skipped_count: data.skipped_count }), 
            5000
          );
        } else {
          openSnackbar(
            t('global_setting.bulk_import_success', 
              'Successfully imported all {{imported_count}} templates!',
              { imported_count: data.imported_count }), 
            4000
          );
        }
      } else if (data.status === 'skipped') {
        // All templates were duplicates
        openSnackbar(
          t('global_setting.bulk_import_all_skipped', 
            'All {{total_count}} templates already exist. No new templates were imported.',
            { total_count: data.total_count }), 
          5000
        );
      } else if (data.status === 'partial_success') {
        openSnackbar(
          t('global_setting.bulk_import_partial', 
            'Imported {{imported_count}} of {{total_count}} templates. {{failed_count}} failed, {{skipped_count}} skipped.',
            { 
              imported_count: data.imported_count, 
              total_count: data.total_count,
              failed_count: data.failed_count,
              skipped_count: data.skipped_count 
            }), 
          6000
        );
        
        // Show details about failed imports if any
        if (data.failed_templates && data.failed_templates.length > 0) {
          setTimeout(() => {
            const failedNames = data.failed_templates.map(t => t.name).slice(0, 3).join(', ');
            const additionalText = data.failed_templates.length > 3 ? ` and ${data.failed_templates.length - 3} more` : '';
            openSnackbar(
              t('global_setting.bulk_import_failed_details', 
                'Failed: {{failedNames}}{{additionalText}}',
                { failedNames: failedNames, additionalText: additionalText }), 
              4000
            );
          }, 1000);
        }
      } else {
        openSnackbar(
          data.message || t('global_setting.bulk_import_error', 'Error importing templates'), 
          4000
        );
      }
      
      // Refresh templates list if any templates were imported
      if (data.imported_count > 0) {
        setTimeout(() => {
          setLoading(true);
          sendCommand('getAllTemplates');
        }, 500);
      }
    }
  };

  // Use our custom WebSocket hook
  const { sendCommand } = useComponentWebSocket(commandHandlers);

  const loadTemplates = useCallback(() => {
    setLoading(true);
    sendCommand('getAllTemplates');
  }, [sendCommand]);
  
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getTemplateSettings: () => {
      return { templates };
    }
  }));

  // Load templates when component mounts
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      console.log('Deleting template:', templateToDelete);
      setDeleting(true); // Start delete loading state
      sendCommand('deleteTemplate', { template_id: templateToDelete.id });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Handle template import
  const handleImportTemplate = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const fileData = JSON.parse(e.target.result);
            
            // Check if this might contain duplicates and show options dialog
            if (fileData.templates && Array.isArray(fileData.templates) && fileData.templates.length > 1) {
              // For multiple templates, show duplicate handling dialog
              setPendingImportData({ type: 'multiple', data: fileData.templates });
              setDuplicateDialogOpen(true);
            } else {
              // For single template, also check for potential duplicates but import directly with skip_duplicates=true
              const templateData = fileData.templates ? fileData.templates[0] : fileData;
              setPendingImportData({ type: 'single', data: templateData });
              setDuplicateDialogOpen(true);
            }
          } catch (error) {
            setImporting(false);
            openSnackbar(t('global_setting.invalid_json_file', 'Invalid JSON file format'), 3000);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  // Handle confirmed import after duplicate dialog
  const handleConfirmedImport = (skipDuplicates) => {
    setDuplicateDialogOpen(false);
    setImporting(true);
    
    if (pendingImportData.type === 'multiple') {
      // Use bulk import for multiple templates
      sendCommand('importTemplates', { 
        templates_data: pendingImportData.data,
        skip_duplicates: skipDuplicates 
      });
      openSnackbar(
        t('global_setting.bulk_import_started', 
          'Starting import of {{count}} templates...',
          { count: pendingImportData.data.length }), 
        2000
      );
    } else {
      // Import single template
      sendCommand('importTemplate', { 
        template_data: pendingImportData.data,
        skip_duplicates: skipDuplicates 
      });
    }
    
    setPendingImportData(null);
  };

  // Handle cancelled import
  const handleCancelledImport = () => {
    setDuplicateDialogOpen(false);
    setPendingImportData(null);
    setImporting(false);
  };

  // Handle template export for a single template
  const handleExportTemplate = (template) => {
    setExportingIds(prev => new Set(prev).add(template.id));
    
    try {
      const exportData = {
        name: template.name || template.template_title,
        description: template.description || template.template_description,
        project_description: template.project_description || '',
        project_goal: template.project_goal || '',
        tags: template.tags || '',
        actions: template.actions || '',
        widgets: template.widgets || '',
        model_preferences: template.model_preferences || '',
        embedded_plugins: template.embedded_plugins || '',
        datasource_suggestions: template.datasource_suggestions || '',
        process_on_file_change: template.process_on_file_change,
        enable_multi_docs_split: template.enable_multi_docs_split,
        file_ingestion_filters: template.file_ingestion_filters,
        prefer_image_creation_time: template.prefer_image_creation_time,
        show_calendar_widget: template.show_calendar_widget,
        show_trend_widget: template.show_trend_widget,
        version: template.version || 1,
        created_at: template.created_at,
        exported_at: new Date().toISOString()
      };
      
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const templateName = (template.name || template.template_title || 'template').replace(/[^a-zA-Z0-9]/g, '_');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateName}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      openSnackbar(t('global_setting.template_exported', 'Template exported successfully'), 3000);
    } catch (error) {
      openSnackbar(t('global_setting.export_error', 'Error exporting template'), 3000);
    } finally {
      setExportingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(template.id);
        return newSet;
      });
    }
  };

  // Handle template export for all templates
  const handleExportAllTemplates = () => {
    if (templates.length === 0) {
      openSnackbar(t('global_setting.no_templates_to_export', 'No templates available to export'), 3000);
      return;
    }

    try {
      // Format templates for export with complete data
      const formattedTemplates = templates.map(template => ({
        name: template.name || template.template_title,
        description: template.description || template.template_description,
        project_description: template.project_description || '',
        project_goal: template.project_goal || '',
        tags: template.tags || '',
        actions: template.actions || '',
        widgets: template.widgets || '',
        model_preferences: template.model_preferences || '',
        embedded_plugins: template.embedded_plugins || '',
        datasource_suggestions: template.datasource_suggestions || '',
        process_on_file_change: template.process_on_file_change,
        enable_multi_docs_split: template.enable_multi_docs_split,
        file_ingestion_filters: template.file_ingestion_filters,
        prefer_image_creation_time: template.prefer_image_creation_time,
        show_calendar_widget: template.show_calendar_widget,
        show_trend_widget: template.show_trend_widget,
        version: template.version || 1,
        created_at: template.created_at
      }));

      const exportData = {
        templates: formattedTemplates,
        exported_at: new Date().toISOString(),
        version: '1.0',
        total_templates: formattedTemplates.length
      };
      
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `all_templates_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      openSnackbar(
        t('global_setting.templates_exported', 
          '{{count}} templates exported successfully',
          { count: formattedTemplates.length }), 
        3000
      );
    } catch (error) {
      openSnackbar(t('global_setting.export_error', 'Error exporting templates'), 3000);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%', width: '100%' }}>
      {/* Header Section */}
  <Box sx={{ mb: 4, textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <TemplateIcon sx={{ fontSize: 32, color: '#5757b0' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5757b0' }}>
            {t('global_setting.templates_section', 'Template Management')}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          {t('global_setting.templates_description', 'Manage your project templates. You can import templates from JSON files, export individual templates, or delete templates you no longer need.')}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: '#f8f9ff',
            border: '1px solid #e8eaff',
            borderRadius: 2,
            mb: 3,
            // Keep header controls visually above list
            position: 'relative',
            zIndex: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {loading || importing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#5757b0' }} />
                  <Typography variant="body2" color="text.secondary">
                    {importing 
                      ? t('global_setting.importing_template', 'Importing template...')
                      : t('global_setting.loading_templates', 'Loading templates...')
                    }
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TemplateIcon sx={{ fontSize: 16, color: '#5757b0' }} />
                  <Typography variant="body2" color="text.secondary">
                    {templates.length === 0
                      ? t('global_setting.no_templates_count', 'No templates found')
                      : templates.length === 1
                      ? t('global_setting.one_template_found', '1 template found')
                      : t('global_setting.templates_found', '{{count}} templates found', { count: templates.length })
                    }
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={t('global_setting.import_template_tooltip', 'Import template from JSON file')}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={importing ? <CircularProgress size={14} /> : <UploadIcon />}
                  onClick={handleImportTemplate}
                  disabled={importing || loading}
                  sx={{
                    borderColor: '#5757b0',
                    color: '#5757b0',
                    '&:hover': {
                      borderColor: '#aaaaca',
                      backgroundColor: 'rgba(87, 87, 176, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#9e9e9e',
                    },
                    minWidth: 'auto',
                    px: 2
                  }}
                >
                  {importing ? t('global_setting.importing', 'Importing...') : t('global_setting.import', 'Import')}
                </Button>
              </Tooltip>

              <Tooltip title={t('global_setting.export_templates_tooltip', 'Export all templates to JSON file')}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAllTemplates}
                  disabled={loading || importing || templates.length === 0}
                  sx={{
                    borderColor: '#5757b0',
                    color: '#5757b0',
                    '&:hover': {
                      borderColor: '#aaaaca',
                      backgroundColor: 'rgba(87, 87, 176, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#9e9e9e',
                    },
                    minWidth: 'auto',
                    px: 2
                  }}
                >
                  {t('global_setting.export_all', 'Export All')}
                </Button>
              </Tooltip>

              <Tooltip title={t('global_setting.refresh_templates_tooltip', 'Refresh template list')}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadTemplates}
                  disabled={loading || importing}
                  sx={{
                    borderColor: '#5757b0',
                    color: '#5757b0',
                    '&:hover': {
                      borderColor: '#aaaaca',
                      backgroundColor: 'rgba(87, 87, 176, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#9e9e9e',
                    },
                    minWidth: 'auto',
                    px: 2
                  }}
                >
                  {t('global_setting.refresh', 'Refresh')}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Templates List */}
      <Fade in={!loading} timeout={300}>
        <Box>
          {templates.length === 0 ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 6, 
                textAlign: 'center', 
                backgroundColor: 'rgba(248, 249, 255, 0.6)',
                border: '2px dashed rgba(87, 87, 176, 0.2)',
                borderRadius: 3,
                backdropFilter: 'blur(10px)'
              }}
            >
              <TemplateIcon sx={{ fontSize: 48, color: 'rgba(87, 87, 176, 0.4)', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                {t('global_setting.no_templates_title', 'No Templates Found')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('global_setting.no_templates_description', 'Create your first project template by saving a project configuration.')}
              </Typography>
            </Paper>
          ) : (
        <Paper
              elevation={0}
              sx={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                boxShadow: '0 8px 32px rgba(87, 87, 176, 0.12)',
                border: '1px solid rgba(87, 87, 176, 0.08)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
          // Ensure the list sits below the header controls and has its own stacking context
          position: 'relative',
          zIndex: 1,
          mt: 1
              }}
            >
              <List sx={{ p: 0, mt: 0 }}>
                {templates.map((template, index) => (
                  <React.Fragment key={template.id}>
                      <ListItem
                      sx={{
                        py: 1.75,
                        px: 2.5,
                        transition: 'box-shadow 0.18s ease, transform 0.18s ease, background-color 0.18s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.035)',
                          boxShadow: '0 6px 18px rgba(87,87,176,0.06)',
                          transform: 'translateY(-2px)'
                        },
                        position: 'relative'
                      }}
                    >
                      <ListItemIcon sx={{ mr: 2, minWidth: 'auto' }}>
                        <Box 
                          sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            backgroundColor: '#f8f9ff',
                            border: '1px solid #e8eaff',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <TemplateIcon sx={{ fontSize: 24, color: '#5757b0' }} />
                        </Box>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: '#222',
                                  fontSize: '1rem',
                                  lineHeight: 1.2
                                }}
                              >
                                {template.name || template.template_title}
                              </Typography>
                            <Chip 
                              size="small" 
                              label={`v${template.version || 1}`}
                              sx={{ 
                                height: 22, 
                                fontSize: '0.7rem',
                                backgroundColor: '#e8eaff',
                                color: '#5757b0',
                                fontWeight: 600,
                                border: '1px solid rgba(87, 87, 176, 0.2)'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                color: 'rgba(0,0,0,0.65)'
                              }}
                            >
                              {template.description || template.template_description ||
                               t('global_setting.no_description', 'No description available')}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              {Array.isArray(template.tags) && template.tags.slice(0,3).map((tag, i) => (
                                <TagChip key={i} tagName={tag} sx={{ mr: 0.5, fontSize: '0.75rem', padding: '2px 6px' }} />
                              ))}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5 }}>
                                <CalendarTodayIcon sx={{ fontSize: 13, color: 'rgba(87, 87, 176, 0.7)' }} />
                                <Typography variant="caption" sx={{ color: 'rgba(87, 87, 176, 0.8)', fontWeight: 500 }}>
                                  {t('global_setting.created_on', 'Created:')} {formatDate(template.created_at)}
                                </Typography>
                              </Box>
                            </Box>
                          </Stack>
                        }
                        sx={{ flex: 1, pr: 12, // increased right padding to avoid overlap with action buttons
                                 maxWidth: 'calc(100% - 160px)' }}
                      />
                      
                      <ListItemSecondaryAction sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 12,
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        alignItems: 'center',
                        zIndex: 3
                      }}>
                        <Stack direction="column" spacing={0.5} sx={{ pr: 0 }}>
                          <Tooltip title={t('global_setting.export_template_tooltip', 'Export this template to JSON file')}>
                              <IconButton
                                size="small"
                                onClick={() => handleExportTemplate(template)}
                                disabled={exportingIds.has(template.id)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  p: 0.5,
                                  color: '#5757b0',
                                  backgroundColor: 'rgba(87, 87, 176, 0.06)',
                                  border: '1px solid rgba(87, 87, 176, 0.12)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(87, 87, 176, 0.12)',
                                    transform: 'scale(1.04)',
                                    borderColor: '#5757b0'
                                  },
                                  '& .MuiSvgIcon-root': { fontSize: 18 },
                                  '&:disabled': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                    color: '#bdbdbd',
                                    borderColor: 'rgba(0, 0, 0, 0.08)'
                                  },
                                  transition: 'all 0.15s ease'
                                }}
                              >
                              {exportingIds.has(template.id) ? (
                                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                              ) : (
                                <GetAppIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title={t('global_setting.delete_template_tooltip', 'Delete this template')}>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(template)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  p: 0.5,
                                  color: '#d32f2f',
                                  backgroundColor: 'rgba(211, 47, 47, 0.06)',
                                  border: '1px solid rgba(211, 47, 47, 0.12)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.12)',
                                    transform: 'scale(1.04)',
                                    borderColor: '#d32f2f'
                                  },
                                  '& .MuiSvgIcon-root': { fontSize: 18 },
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                          </Tooltip>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    {index < templates.length - 1 && (
                      <Divider 
                        sx={{ 
                          mx: 3,
                          borderColor: 'rgba(87, 87, 176, 0.1)' 
                        }} 
                      />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Fade>

      {/* Enhanced Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              sx={{ 
                p: 1, 
                borderRadius: '50%', 
                backgroundColor: '#ffebee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <WarningAmberIcon sx={{ color: '#d32f2f', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
              {t('global_setting.confirm_delete_template', 'Delete Template')}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              {t('global_setting.delete_warning', 'This action cannot be undone. The template will be permanently removed.')}
            </Typography>
          </Alert>
          
          <DialogContentText sx={{ color: 'text.primary', fontSize: '1rem' }}>
            {t('global_setting.confirm_delete_template_message', 
              'Are you sure you want to delete the template "{templateName}"?', {
                templateName: templateToDelete?.name || templateToDelete?.template_title
              }
            )}
          </DialogContentText>
          
          {templateToDelete && (
            <Paper 
              elevation={0} 
              sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: '#f8f9ff',
                border: '1px solid #e8eaff',
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TemplateIcon sx={{ color: '#5757b0', fontSize: 20 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                    {templateToDelete.name || templateToDelete.template_title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('global_setting.version', 'Version:')} {templateToDelete.version || 1} • 
                    {t('global_setting.created_on', 'Created:')} {formatDate(templateToDelete.created_at)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={handleDeleteCancel} 
            variant="outlined"
            disabled={deleting}
            sx={{
              borderColor: '#e0e0e0',
              color: '#666',
              '&:hover': {
                borderColor: '#bdbdbd',
                backgroundColor: '#f5f5f5'
              },
              '&:disabled': {
                borderColor: '#f0f0f0',
                color: '#bdbdbd'
              }
            }}
          >
            {t('global_setting.cancel', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            disabled={deleting}
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c'
              },
              '&:disabled': {
                backgroundColor: '#ffcdd2',
                color: '#d32f2f'
              },
              fontWeight: 600
            }}
          >
            {deleting ? t('global_setting.deleting', 'Deleting...') : t('global_setting.delete_template', 'Delete Template')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Handling Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={handleCancelledImport}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              sx={{ 
                p: 1, 
                borderRadius: '50%', 
                backgroundColor: '#fff3e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <UploadIcon sx={{ color: '#f57c00', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
              {t('global_setting.import_options', 'Import Options')}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 2 }}>
          <DialogContentText sx={{ color: 'text.primary', fontSize: '1rem', mb: 3 }}>
            {pendingImportData?.type === 'multiple' 
              ? t('global_setting.import_multiple_message', 
                  `You are importing ${pendingImportData?.data?.length || 0} templates. How should duplicates be handled?`,
                  { count: pendingImportData?.data?.length || 0 })
              : t('global_setting.import_single_message', 
                  'You are importing a template. How should duplicates be handled?')
            }
          </DialogContentText>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                backgroundColor: '#f1f8e9',
                border: '1px solid #c8e6c9',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#e8f5e8'
                }
              }}
              onClick={() => handleConfirmedImport(true)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box 
                  sx={{ 
                    mt: 0.5,
                    p: 0.5, 
                    borderRadius: '50%', 
                    backgroundColor: '#4caf50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 0.5 }}>
                    {t('global_setting.skip_duplicates', 'Skip Duplicates (Recommended)')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('global_setting.skip_duplicates_description', 
                      'Only import templates with new names. Existing templates will be left unchanged. This prevents accidental overwriting of your templates.')}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                backgroundColor: '#fff3e0',
                border: '1px solid #ffcc02',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#ffecb3'
                }
              }}
              onClick={() => handleConfirmedImport(false)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box 
                  sx={{ 
                    mt: 0.5,
                    p: 0.5, 
                    borderRadius: '50%', 
                    backgroundColor: '#ff9800',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>!</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#ef6c00', mb: 0.5 }}>
                    {t('global_setting.allow_duplicates', 'Import All (Allow Duplicates)')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('global_setting.allow_duplicates_description', 
                      'Import all templates, even if they have the same name as existing ones. This may create duplicate templates with identical names.')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={handleCancelledImport} 
            variant="outlined"
            sx={{
              borderColor: '#e0e0e0',
              color: '#666',
              '&:hover': {
                borderColor: '#bdbdbd',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            {t('global_setting.cancel', 'Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export { TemplateSettings };
